import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type {
  ContentDetailsParams,
  ContentSelection,
  SearchResultItem,
} from '@bizzdesign/data-access-search';
import type { ActionButton } from '@bizzdesign/shared/models';
import { type SearchScope, type SearchSortInput } from '@bizzdesign/sdk-search';
import { PaginationComponent } from '@bizzdesign/ui-pagination';
import { ObjectSearchResultItemComponent } from '../object-search-result-item/object-search-result-item.component';
import { ObjectSearchResultsHeaderComponent } from '../object-search-results-header/object-search-results-header.component';
import {
  OBJECT_RESULTS_SYSTEM_COLUMN_CATALOGUE,
  type ObjectResultsColumnCatalogue,
} from '../../utils/object-results-columns.utils';
import {
  ObjectSearchResultsTableComponent,
  type ManageWorkspaceAccess,
  type ObjectResultsTableLayout,
  type ObjectResultsTableSelection,
} from '../object-search-results-table/object-search-results-table.component';
import {
  SearchTabEmptyStateComponent,
  type SearchTabEmptyStateLink,
} from '../search-tab-empty-state/search-tab-empty-state.component';
import { ObjectSearchResultsLoadingComponent } from './object-search-results-loading.component';
import { ObjectSearchResultsComponentStore } from './object-search-results.component.store';

@Component({
  selector: 'bd-object-search-results',
  imports: [
    SearchTabEmptyStateComponent,
    FormsModule,
    PaginationComponent,
    ObjectSearchResultsLoadingComponent,
    ObjectSearchResultsHeaderComponent,
    ObjectSearchResultsTableComponent,
    ObjectSearchResultItemComponent,
  ],
  templateUrl: './object-search-results.component.html',
  styleUrl: './object-search-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectSearchResultsComponent {
  @ViewChild('searchResultItemsWrapper', { read: ElementRef })
  searchResultItemsWrapper: ElementRef | undefined;
  readonly store = inject(ObjectSearchResultsComponentStore);

  @Input() set searchResultItems(value: SearchResultItem[]) {
    this.store.updateSearchResultItems(value);
  }

  readonly isDrawerOpen = input(false);
  readonly activeItemId = input('');
  @Input() filtersHeight: number = 0;
  @Input() totalSearchResultsCount: number = 0;
  @Input() currentSort: SearchSortInput | undefined = undefined;
  private _addToFrameWithoutSelectionEnabled: boolean = false;
  @Input() set addToFrameWithoutSelectionEnabled(value: boolean) {
    this._addToFrameWithoutSelectionEnabled = value;
    this.setHeaderActions();
  }
  get addToFrameWithoutSelectionEnabled(): boolean {
    return this._addToFrameWithoutSelectionEnabled;
  }
  readonly columnCatalogue = input<ObjectResultsColumnCatalogue>(
    OBJECT_RESULTS_SYSTEM_COLUMN_CATALOGUE,
  );
  readonly manageWorkspaceActionDisabled = input(false);
  readonly manageWorkspaceNoAccessTooltip = input('');
  @Input() objectResultsMode: 'list' | 'table' = 'list';
  @Input() aiSearchActive: boolean = false;
  @Input() emptyStateLinks: SearchTabEmptyStateLink[] = [];

  @Output() contentOpen: EventEmitter<ContentSelection> = new EventEmitter();
  @Output() openSearchResultItem: EventEmitter<SearchResultItem> =
    new EventEmitter();
  @Output() deleteSearchResultItem: EventEmitter<SearchResultItem> =
    new EventEmitter();
  @Output() openContentDetails: EventEmitter<ContentDetailsParams> =
    new EventEmitter();
  @Output() reviewAppliedFiltersClick: EventEmitter<void> = new EventEmitter();
  @Output() clearFiltersClick: EventEmitter<void> = new EventEmitter();
  @Output() objectSelectionChange = new EventEmitter<ContentSelection>();
  @Output() contentEditData: EventEmitter<ContentSelection> =
    new EventEmitter();
  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() switchScope: EventEmitter<SearchScope> =
    new EventEmitter<SearchScope>();
  @Output() sortChange: EventEmitter<SearchSortInput | undefined> =
    new EventEmitter<SearchSortInput | undefined>();

  headerActions: ActionButton[] = [];

  readonly tableLayout: ObjectResultsTableLayout = {
    showHeader: true,
    showSelectionColumn: true,
    scrollable: true,
  };
  readonly tableSelection = computed<ObjectResultsTableSelection>(() => ({
    isSelectionMode:
      this.store.searchResultsGlobalStoreProperties().isSelectionMode,
    selectedItems: this.store.selectedItems(),
    activeItemId: this.isDrawerOpen() ? this.activeItemId() : '',
  }));
  readonly tableManageWorkspaceAccess = computed<ManageWorkspaceAccess>(() => ({
    actionDisabled: this.manageWorkspaceActionDisabled(),
    noAccessTooltip: this.manageWorkspaceNoAccessTooltip(),
  }));

  constructor() {
    effect(() => {
      this.objectSelectionChange.emit(this.store.getObjectSelection());
    });
    this.setHeaderActions();
  }

  onOpenContentDetails(panelParams: ContentDetailsParams): void {
    this.openContentDetails.emit(panelParams);
  }

  onOpenSearchResultItem(resultItem: SearchResultItem): void {
    this.openSearchResultItem.emit(resultItem);
  }

  setHeaderActions(): void {
    this.headerActions = [
      {
        labelKey: 'editData',
        tScope: 'featureSearch',
        onClick: () => this.handleContentEditData(),
        type: 'tertiary',
        loading$:
          this.store.searchResultsGlobalStoreProperties().editDataLoading$,

        actionKey: 'manage',
        subject: 'repositoryData',
        disabled: this.store.editDataButtonDisabled(),
        tooltipKey: this.store.editDataButtonDisabled()
          ? 'editDataDisabledTooltip'
          : '',
      },
    ];

    if (!this.addToFrameWithoutSelectionEnabled) {
      this.headerActions.push({
        labelKey: 'addTo',
        tScope: 'featureSearch',
        onClick: () => this.handleContentOpen(),
        group: 'brand',
        actionKey: 'addToWorkspace',
        subject: 'objects',
      });
    }
  }

  onItemSelectionToggled(event: { shiftKey: boolean; itemId: string }): void {
    this.store.updateToggledItemIndexes(event.shiftKey, event.itemId);
  }

  onSelectedItemsChange(newSelectedItems: SearchResultItem[]): void {
    this.store.updateSelectedItems(newSelectedItems);
    if (this.store.selectAllApplied()) {
      this.store.updateUnselectedItemsFromCurrentSelection();
    }
    this.setHeaderActions();
  }

  onSelectAllClick(): void {
    this.onSelectAllChange(true);
  }

  onSelectAllChange(checked: boolean): void {
    if (checked) {
      this.store.applySelectAll();
      this.store.searchResultsGlobalStore.updateIsSelectionMode(true);
      this.store.updateSearchResultItemsAfterSelection([]);
    } else {
      this.store.resetSelectionStates(false);
      this.store.searchResultsGlobalStore.updateIsSelectionMode(false);
      this.store.updateSearchResultItemsAfterSelection([]);
    }
    this.setHeaderActions();
  }

  handleContentOpen(): void {
    this.contentOpen.emit({
      selectedItems: this.store.selectedItems(),
      fetchAllItems: this.store.selectAllApplied(),
      unselectedItems: this.store.unselectedItems(),
      selectedItemsCount: this.store.numberOfSelectedItems(),
    });
  }

  handleContentEditData(): void {
    this.contentEditData.emit({
      selectedItems: this.store.selectedItems(),
      fetchAllItems: this.store.selectAllApplied(),
      unselectedItems: this.store.unselectedItems(),
      selectedItemsCount: this.store.numberOfSelectedItems(),
    });
  }

  protected reviewAppliedFilters = (): void => {
    this.reviewAppliedFiltersClick.emit();
  };

  protected clearFilters = (): void => {
    this.clearFiltersClick.emit();
  };

  onPageChange(pageNumber: number): void {
    this.getScrollableResultsElement()?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    this.pageChange.emit(pageNumber);
  }

  onSortChange(sorting: SearchSortInput | undefined): void {
    this.getScrollableResultsElement()?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    this.sortChange.emit(sorting);
  }

  private getScrollableResultsElement(): HTMLElement | undefined {
    const wrapper = this.searchResultItemsWrapper?.nativeElement as
      | HTMLElement
      | undefined;
    if (!wrapper) {
      return undefined;
    }
    if (this.objectResultsMode === 'table') {
      return (wrapper.querySelector('.p-datatable-table-container') ??
        wrapper) as HTMLElement;
    }
    return wrapper;
  }
}
